import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { IoSearch, IoEyeOutline, IoChevronUp, IoChevronDown } from "react-icons/io5";
import { toast } from "react-toastify";
import { getItemsPerPage } from "../../../utils/CommonFunctions";
import JobActivityService from "../../../../services/JobActivityService";
import { colors } from "../../../config/colors";
import { Menu, Transition } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/solid';
import { getJobTitleLabel } from "../../../utils/jobTitleLabel";

const ListJobActivity = () => {
  const jobLabel = getJobTitleLabel();
  const navigate = useNavigate();
  const [pageVal, setPageVal] = useState(1);
  const [totalActivities, setTotalActivities] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [activityList, setActivityList] = useState([]);
  const [searchVal, setSearchVal] = useState("");
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const activitiesPerPage = getItemsPerPage();
  const [selectedStatus, setSelectedStatus] = useState('ALL');

  const SEARCH_DEBOUNCE_DELAY = 500;

  const statusOptions = [
    { label: 'All Activities', value: 'ALL' },
    { label: 'Pending', value: 'PENDING' },
    { label: 'Approved', value: 'APPROVED' },
    { label: 'Rejected', value: 'REJECTED' },
  ];

  const debouncedSearch = useCallback(
    debounce((value) => {
      setSearchVal(value);
    }, SEARCH_DEBOUNCE_DELAY),
    []
  );

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setInputValue(value);
    debouncedSearch(value);
  };

  const handlePreviousPageClick = () => {
    if (pageVal === 1) return;
    setPageVal(pageVal - 1);
  };

  const handleNextPageClick = () => {
    if (pageVal >= totalPages) return;
    setPageVal(pageVal + 1);
  };

  const fetchActivityList = async () => {
    setIsLoading(true);
    !activityList && setLoading(true);
    try {
      
      const response = await JobActivityService.getJobActivities(
        pageVal,
        activitiesPerPage,
        searchVal,
        selectedStatus
      );

      if (response.success) {
        const { data } = response;
        setActivityList(data.activities);
        setTotalActivities(data.pagination.total);
        setTotalPages(data.pagination.totalPages);
      } else {
        throw new Error("Failed to fetch activities");
      }
    } catch (error) {
      console.error("Error fetching activity list:", error);
      toast.error("Failed to fetch activities");
    } finally {
      setIsLoading(false);
      setLoading(false);
    }
  };

  const handleStatusChange = (status) => {
    setSelectedStatus(status);
  };

  useEffect(() => {
    fetchActivityList();
  }, [pageVal, selectedStatus]);

  useEffect(() => {
    if (pageVal === 1) {
      fetchActivityList();
    } else {
      setPageVal(1);
    }
  }, [searchVal]);

  function debounce(func, wait) {
    let timeout;
    const debouncedFunc = function (...args) {
      const context = this;
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(context, args), wait);
    };
    debouncedFunc.cancel = function () {
      clearTimeout(timeout);
    };
    return debouncedFunc;
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  const formatTime = (timeString) => {
    return timeString;
  };

  const handleViewActivity = (jobId) => {
    navigate(`/view-job/${jobId}`);
  };

  const handleApproveActivity = async (activityId, isApproved) => {
    try {
      const response = await JobActivityService.approveJobActivity(activityId, isApproved);
      if (response.success) {
        toast.success(isApproved ? "Activity approved successfully" : "Activity rejected successfully");
        fetchActivityList();
      }
    } catch (error) {
      console.error("Error approving/rejecting activity:", error);
      toast.error("Failed to update activity status");
    }
  };

  return (
    <div className="h-[calc(100vh-4rem)] overflow-y-auto bg-gradient-to-b from-white to-accent-light/10 p-6">
      {/* Header Section */}
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-primary-headings mb-2">
          {`${jobLabel} Activity Management`}
        </h2>
        <p className="text-sm text-primary-light mb-6">
          {`Track and manage ${jobLabel.toLowerCase()} activity changes`}
        </p>

        {/* Search Bar */}
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 flex flex-col sm:flex-row gap-4 items-center">
          <div className="relative flex-1">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <IoSearch className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              value={inputValue}
              onChange={handleSearchChange}
              className="block w-full pl-10 pr-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-light focus:border-transparent text-sm"
              placeholder="Search activities..."
            />
          </div>
        </div>

        {/* Status Filter */}
        <div className="mt-6">
          <div className="hidden md:flex bg-white rounded-xl p-2 shadow-sm border border-gray-100">
            {statusOptions.map((status) => (
              <button
                key={status.value}
                onClick={() => handleStatusChange(status.value)}
                className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${
                  selectedStatus === status.value
                  ? 'bg-primary-headings text-white'
                  : 'text-gray-600 hover:bg-gray-100'
                }`}
                style={{
                  backgroundColor: selectedStatus === status.value ? colors.primary.headingsBody : '',
                }}
              >
                {status.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Table Section */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden max-h-[calc(100vh-480px)] flex flex-col mb-8">
        <div className="overflow-x-auto flex-1">
          <table className="w-full">
            <thead className="bg-white z-10 after:absolute after:inset-x-0 after:bottom-0 after:h-[1px] after:bg-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-primary-headings uppercase tracking-wider sticky top-0 bg-gray-100">{`${jobLabel} Title`}</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-primary-headings uppercase tracking-wider sticky top-0 bg-gray-100">Type</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-primary-headings uppercase tracking-wider sticky top-0 bg-gray-100">Status</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-primary-headings uppercase tracking-wider sticky top-0 bg-gray-100">Created At</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-primary-headings uppercase tracking-wider sticky top-0 bg-gray-100">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="5" className="px-4 py-8">
                    <div className="flex items-center justify-center">
                      <div className="w-8 h-8 border-4 border-gray-300 rounded-full animate-spin" style={{ borderTopColor: colors.primary.headingsBody }}></div>
                    </div>
                  </td>
                </tr>
              ) : (
                <>
                  {activityList?.map((activity) => (
                    <tr 
                      key={activity._id} 
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => activity.isApproved === null && handleViewActivity(activity.jobId._id)}
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center">
                          <span className="text-sm font-medium text-primary-headings truncate">
                            {activity.jobId.title.length > 50 ? activity.jobId.title.slice(0, 50) + '...' : activity.jobId.title}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-gray-900">
                          {activity.type ? activity.type.toLowerCase().replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : ''}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span 
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            activity.isApproved === null
                              ? 'bg-yellow-100 text-yellow-800'
                              : activity.isApproved
                                ? 'bg-green-100 text-green-800'
                                : 'bg-red-100 text-red-800'
                          }`}
                        >
                          {activity.isApproved === null
                            ? 'Pending'
                            : activity.isApproved
                              ? 'Approved'
                              : 'Rejected'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-gray-900">
                          {formatDate(activity.createdAt)}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {/* {activity.isApproved === null && ( */}
                          <div className="flex justify-center space-x-2">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleViewActivity(activity.jobId._id);
                              }}
                              className="p-1.5 hover:bg-blue-50 rounded-lg transition-colors duration-200"
                              title="View"
                              disabled={activity.isApproved !== null}
                            >
                              <IoEyeOutline size={18} className={`${activity.isApproved !== null ? 'text-gray-400' : 'text-blue-500'}`} />
                            </button>
                          </div>
                        {/* )} */}
                      </td>
                    </tr>
                  ))}

                  {activityList.length === 0 && (
                    <tr>
                      <td colSpan="5" className="px-4 py-8 text-center">
                        <div className="flex flex-col items-center justify-center">
                          <IoSearch size={48} className="text-gray-400 mb-4" />
                          <h3 className="text-lg font-medium text-gray-900 mb-2">No activities found</h3>
                          <p className="text-gray-500">Try adjusting your search or filters to find what you're looking for.</p>
                        </div>
                      </td>
                    </tr>
                  )}
                </>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {!loading && activityList.length > 0 && (
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <p className="text-sm text-gray-700">
                Showing <span className="font-medium">{activitiesPerPage * (pageVal - 1) + 1}</span> to{' '}
                <span className="font-medium">{Math.min(activitiesPerPage * pageVal, totalActivities)}</span> of{' '}
                <span className="font-medium">{totalActivities}</span> results
              </p>
              <nav className="flex items-center gap-2">
                <button
                  onClick={handlePreviousPageClick}
                  disabled={pageVal === 1}
                  className={`px-3 py-1 rounded-md text-sm font-medium ${
                    pageVal === 1
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-primary-headings text-white hover:bg-opacity-90'
                  }`}
                >
                  Previous
                </button>
                <span className="text-sm text-gray-700">
                  Page {pageVal} of {totalPages}
                </span>
                <button
                  onClick={handleNextPageClick}
                  disabled={pageVal >= totalPages}
                  className={`px-3 py-1 rounded-md text-sm font-medium ${
                    pageVal >= totalPages
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-primary-headings text-white hover:bg-opacity-90'
                  }`}
                >
                  Next
                </button>
              </nav>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ListJobActivity; 